import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  MessageCircle,
  Eye,
  Calendar,
  Edit2,
  Trash2,
  Send,
  Heart,
  Share2,
  Clock,
  User,
  Building,
  AlertTriangle,
  Info,
  Megaphone,
  GraduationCap,
  PartyPopper,
  FileText,
} from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { LoadingSpinner } from "@/components/common/loading-spinner";

import announcementService from "@/services/announcement.service";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { formatDate, formatTimeAgo } from "@/lib/utils";

const typeIcons = {
  info: Info,
  urgente: AlertTriangle,
  evenement: Calendar,
  procedure: FileText,
  celebration: PartyPopper,
  formation: GraduationCap,
  autre: Megaphone,
};

const typeColors = {
  info: "bg-blue-100 text-blue-800 border-blue-200",
  urgente: "bg-red-100 text-red-800 border-red-200",
  evenement: "bg-purple-100 text-purple-800 border-purple-200",
  procedure: "bg-gray-100 text-gray-800 border-gray-200",
  celebration: "bg-pink-100 text-pink-800 border-pink-200",
  formation: "bg-green-100 text-green-800 border-green-200",
  autre: "bg-orange-100 text-orange-800 border-orange-200",
};

const priorityColors = {
  basse: "bg-gray-100 text-gray-600",
  normale: "bg-blue-100 text-blue-600",
  haute: "bg-orange-100 text-orange-600",
  critique: "bg-red-100 text-red-600",
};

const AnnouncementDetailModal = ({
  isOpen,
  onClose,
  announcement,
  onEdit,
  onDelete,
  canEdit = false,
}) => {
  const { user } = useAuth();
  const { toast } = useToast();

  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loadingComments, setLoadingComments] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [commentsPagination, setCommentsPagination] = useState({});

  // Charger les commentaires
  const fetchComments = async () => {
    if (!announcement?.id) return;

    try {
      setLoadingComments(true);
      const response = await announcementService.getComments(announcement.id, {
        page: 1,
        limit: 20,
      });
      setComments(response.data.data);
      setCommentsPagination(response.data.pagination);
    } catch (error) {
      console.error("Erreur lors du chargement des commentaires:", error);
    } finally {
      setLoadingComments(false);
    }
  };

  // Charger les commentaires quand l'annonce change
  useEffect(() => {
    if (isOpen && announcement) {
      fetchComments();
    }
  }, [isOpen, announcement]);

  // Soumettre un commentaire
  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      setSubmittingComment(true);
      await announcementService.addComment(announcement.id, newComment.trim());

      setNewComment("");
      fetchComments(); // Recharger les commentaires

      toast({
        title: "Succès",
        description: "Commentaire ajouté avec succès",
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible d'ajouter le commentaire",
        variant: "destructive",
      });
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDelete = () => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cette annonce ?")) {
      onDelete(announcement.id);
      onClose();
    }
  };

  if (!announcement) return null;

  const TypeIcon = typeIcons[announcement.type_message] || Megaphone;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="pb-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3 flex-1">
              <div
                className={`p-3 rounded-lg ${
                  typeColors[announcement.type_message]
                }`}
              >
                <TypeIcon className="h-6 w-6" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-2">
                  <Badge className={typeColors[announcement.type_message]}>
                    {announcement.type_message}
                  </Badge>
                  <Badge className={priorityColors[announcement.priorite]}>
                    {announcement.priorite}
                  </Badge>
                  {!announcement.isRead && (
                    <Badge variant="destructive">Nouveau</Badge>
                  )}
                </div>
                <DialogTitle className="text-2xl font-bold leading-tight">
                  {announcement.titre}
                </DialogTitle>
                <DialogDescription className="mt-2">
                  <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                    <div className="flex items-center space-x-2">
                      <Avatar className="h-6 w-6">
                        <AvatarImage
                          src={announcement.publieur?.photo_profil}
                        />
                        <AvatarFallback className="text-xs">
                          {announcement.publieur?.prenom?.[0]}
                          {announcement.publieur?.nom?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <span className="font-medium">
                        {announcement.publieur?.prenom}{" "}
                        {announcement.publieur?.nom}
                      </span>
                    </div>
                    <Separator orientation="vertical" className="h-4" />
                    <div className="flex items-center space-x-1">
                      <Clock className="h-3 w-3" />
                      <span>
                        {formatTimeAgo(announcement.date_publication)}
                      </span>
                    </div>
                    <Separator orientation="vertical" className="h-4" />
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-1">
                        <Eye className="h-3 w-3" />
                        <span>
                          {announcement._count?.lectures || 0} lectures
                        </span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <MessageCircle className="h-3 w-3" />
                        <span>
                          {announcement._count?.commentaires || 0} commentaires
                        </span>
                      </div>
                    </div>
                  </div>
                </DialogDescription>
              </div>
            </div>
            {canEdit && (
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEdit(announcement)}
                >
                  <Edit2 className="h-4 w-4 mr-2" />
                  Modifier
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDelete}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Supprimer
                </Button>
              </div>
            )}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          <ScrollArea className="flex-1">
            <div className="space-y-6 pr-4">
              {/* Contenu principal */}
              <Card>
                <CardContent className="p-6">
                  <div className="prose prose-sm max-w-none">
                    <div className="whitespace-pre-wrap leading-relaxed">
                      {announcement.contenu}
                    </div>
                  </div>

                  {/* Métadonnées supplémentaires */}
                  <div className="mt-6 pt-4 border-t space-y-3">
                    {announcement.date_expiration && (
                      <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        <span>
                          Expire le {formatDate(announcement.date_expiration)}
                        </span>
                      </div>
                    )}

                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      <Eye className="h-4 w-4" />
                      <span>Visible par : {announcement.visible_par}</span>
                    </div>

                    {announcement.publieur?.departement && (
                      <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                        <Building className="h-4 w-4" />
                        <span>
                          Département : {announcement.publieur.departement.nom}
                        </span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Section commentaires */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <MessageCircle className="h-5 w-5 mr-2" />
                    Commentaires ({announcement._count?.commentaires || 0})
                  </h3>

                  {/* Formulaire d'ajout de commentaire */}
                  <form onSubmit={handleSubmitComment} className="mb-6">
                    <div className="space-y-3">
                      <Textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Ajouter un commentaire..."
                        className="min-h-[80px]"
                        maxLength={1000}
                      />
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-muted-foreground">
                          {newComment.length}/1000 caractères
                        </span>
                        <Button
                          type="submit"
                          size="sm"
                          disabled={!newComment.trim() || submittingComment}
                        >
                          {submittingComment ? (
                            <LoadingSpinner className="h-4 w-4 mr-2" />
                          ) : (
                            <Send className="h-4 w-4 mr-2" />
                          )}
                          Publier
                        </Button>
                      </div>
                    </div>
                  </form>

                  <Separator className="mb-6" />

                  {/* Liste des commentaires */}
                  <div className="space-y-4">
                    {loadingComments ? (
                      <div className="flex justify-center py-8">
                        <LoadingSpinner className="h-6 w-6" />
                      </div>
                    ) : comments.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>Aucun commentaire pour le moment</p>
                        <p className="text-sm">
                          Soyez le premier à commenter !
                        </p>
                      </div>
                    ) : (
                      <AnimatePresence>
                        {comments.map((comment) => (
                          <motion.div
                            key={comment.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="flex space-x-3 p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                          >
                            <Avatar className="h-8 w-8 flex-shrink-0">
                              <AvatarImage
                                src={comment.employe?.photo_profil}
                              />
                              <AvatarFallback className="text-xs">
                                {comment.employe?.prenom?.[0]}
                                {comment.employe?.nom?.[0]}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2 mb-1">
                                <span className="font-medium text-sm">
                                  {comment.employe?.prenom}{" "}
                                  {comment.employe?.nom}
                                </span>
                                <Badge variant="outline" className="text-xs">
                                  {comment.employe?.role}
                                </Badge>
                                <span className="text-xs text-muted-foreground">
                                  {formatTimeAgo(comment.date_commentaire)}
                                </span>
                              </div>
                              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                {comment.commentaire}
                              </p>
                            </div>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    )}

                    {/* Bouton "Charger plus" si nécessaire */}
                    {commentsPagination.hasNextPage && (
                      <div className="text-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            // TODO: Implémenter le chargement de plus de commentaires
                          }}
                        >
                          Charger plus de commentaires
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </ScrollArea>
        </div>

        {/* Actions en bas */}
        <div className="border-t pt-4 mt-4">
          <div className="flex justify-between items-center">
            <div className="flex space-x-3">
              <Button variant="outline" size="sm">
                <Heart className="h-4 w-4 mr-2" />
                Aimer
              </Button>
              <Button variant="outline" size="sm">
                <Share2 className="h-4 w-4 mr-2" />
                Partager
              </Button>
            </div>
            <Button variant="outline" onClick={onClose}>
              Fermer
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AnnouncementDetailModal;
